/**
 * Admin UI type definitions.
 * Mirrors backend entities from @cluster/types with UI-specific extensions.
 */

// ─── Auth ────────────────────────────────────────────────────────────────────

export interface User {
  _id: string;
  email: string;
  username: string;
  role: UserRole;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export type UserRole = 'admin' | 'user' | 'operator' | 'viewer';

export interface LoginRequest {
  email?: string;
  username?: string;
  password: string;
}

/** Final session shape returned by login / MFA completion (legacy path unchanged). */
export interface LoginTokensResponse {
  accessToken: string;
  refreshToken: string;
  policy: SessionPolicy;
}

/**
 * Intermediate response when the account requires a second factor
 * (auth-service `MfaChallengeResponse`). No access/refresh token is present
 * in this branch. `preAuthJwt` is a short-lived, single-use challenge and
 * MUST stay in memory only (never persisted). Enrollment is derived from
 * `status`: NOT_ENROLLED → run PUT /auth/mfa/enroll → POST /auth/mfa/confirm;
 * ENROLLED → POST /auth/login/mfa.
 */
export interface MfaRequiredResponse {
  mfaRequired: true;
  status: 'NOT_ENROLLED' | 'ENROLLED';
  preAuthJwt: string;
  /** Challenge lifetime in seconds (server-issued). */
  preAuthTtlSeconds: number;
}

export type LoginResponse = LoginTokensResponse | MfaRequiredResponse;

/**
 * Response of PUT /auth/mfa/enroll (auth-service `EnrollResult`).
 * `otpauthUri` embeds the TOTP secret and `recoveryCodes` are shown ONCE —
 * both memory-only, never persisted. `nextPreAuthJwt` is the refreshed
 * single-use challenge for the first POST /auth/mfa/confirm.
 */
export interface MfaEnrollResponse {
  otpauthUri: string;
  /** Base32 secret — returned once; display/QR material (memory only). */
  secret?: string;
  recoveryCodes: string[];
  nextPreAuthJwt: string;
}

/**
 * Response of POST /auth/mfa/confirm (auth-service `MfaConfirmResponse`).
 * - First sequential code: `{ confirmed: false, nextPreAuthJwt }` — no
 *   session; retry with the NEXT authenticator code and the refreshed ticket.
 * - Second sequential code: `{ confirmed: true, accessToken, refreshToken,
 *   policy }` — the pending login completes via the sole issuer with the
 *   `mfaVerified` claim.
 */
export type MfaConfirmResponse =
  { confirmed: false; nextPreAuthJwt?: string } | ({ confirmed: true } & LoginTokensResponse);

export interface RefreshResponse {
  accessToken: string;
  refreshToken: string;
}

/**
 * Response of POST /auth/sessions/org (auth-service switch-org-session).
 * Minted ONLY after server-side authorization for `orgId`; the pair fully
 * replaces the current session tokens. `orgId`/`organizationName` are
 * display metadata returned by the server — never an auth credential,
 * never trusted client-side, and never persisted separately from the JWT.
 */
export interface OrgSessionResponse {
  accessToken: string;
  refreshToken: string;
  orgId: string;
  organizationName?: string;
  /** Org+workspace sessions: server-verified workspace scope (ADDITIVE). */
  workspaceId?: string;
}

export interface SessionPolicy {
  name: string;
  ownerUserId: string;
  statements: SessionPolicyStatement[];
  expiresAt: string;
  version: number;
  isActive: boolean;
  isActiveForUser: boolean;
}

export interface SessionPolicyStatement {
  effect: 'Allow' | 'Deny';
  actions: string[];
  resources: string[];
}

// ─── Policy ──────────────────────────────────────────────────────────────────

export interface Policy {
  _id: string;
  name: string;
  description?: string;
  ownerUserId: string;
  parentPolicyId?: string;
  statements: PolicyStatement[];
  version: number;
  isActive: boolean;
  isActiveForUser: boolean;
  priority: number;
  expiresAt?: string;
  cacheTtl: number;
  cascadeDisabledAt?: string;
  cascadeVersion: number;
  createdAt: string;
  updatedAt: string;
}

export interface PolicyStatement {
  sid?: string;
  effect: PolicyEffect;
  actions: string[];
  resources: string[];
  conditions?: PolicyCondition[];
  principals?: string[];
  notActions?: string[];
  notResources?: string[];
}

export interface PolicyCondition {
  /** JSEP expression form (authorization model — epic-6 PB-1). */
  expression?: string;
  description?: string;
  /** Legacy { operator, key, value } form. */
  operator?: string;
  key?: string;
  value?: string | number | boolean | string[];
}

export type PolicyEffect = 'Allow' | 'Deny';

export interface PolicyEvaluationRequest {
  policyId: string;
  action: string;
  resource: string;
  context?: Record<string, unknown>;
}

export interface PolicyEvaluationResult {
  allowed: boolean;
  matchedStatement?: PolicyStatement;
  reason: string;
}

// ─── Service Subject ─────────────────────────────────────────────────────────

export interface ServiceSubject {
  _id: string;
  serviceName: string;
  queueName: string;
  status: ServiceStatus;
  endpoints: EndpointDescriptor[];
  metadata?: Record<string, unknown>;
  lastHeartbeat?: string;
  registeredAt: string;
  deregisteredAt?: string;
}

export interface EndpointDescriptor {
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' | 'ALL';
  path: string;
  description?: string;
  requestSchema?: Record<string, unknown>;
  responseSchema?: Record<string, unknown>;
  isPublic: boolean;
  rateLimit?: {
    windowMs: number;
    maxRequests: number;
  };
}

export type ServiceStatus = 'registered' | 'active' | 'paused' | 'failed' | 'deregistered';

// ─── Jobs ────────────────────────────────────────────────────────────────────

export interface Job {
  id: string;
  name: string;
  queueName: string;
  status: JobStatus;
  data: Record<string, unknown>;
  progress: number;
  result?: unknown;
  failedReason?: string;
  attemptsMade: number;
  attemptsAllowed: number;
  timestamp: number;
  processedOn?: number;
  finishedOn?: number;
  parentId?: string;
}

export type JobStatus =
  'queued' | 'active' | 'running' | 'completed' | 'failed' | 'delayed' | 'paused' | 'cancelled';

export interface JobCounts {
  waiting: number;
  active: number;
  completed: number;
  failed: number;
  delayed: number;
  paused: number;
}

// ─── Dashboard ───────────────────────────────────────────────────────────────

export interface DashboardStats {
  activeUsers: number;
  servicesRunning: number;
  jobsInQueue: number;
  policyEvaluations: number;
}

export interface AuditEvent {
  _id: string;
  action: string;
  actor: string;
  target: string;
  timestamp: string;
  metadata?: Record<string, unknown>;
}

// ─── WebSocket ───────────────────────────────────────────────────────────────

export interface WsCommand {
  service: string;
  method: string;
  payload: unknown;
}

export interface WsSubscription {
  topic: string;
}

export interface WsDeltaEvent {
  op: 'patch' | 'replace' | 'delete';
  path: string[];
  value?: unknown;
}

// ─── API ─────────────────────────────────────────────────────────────────────

export interface PaginatedResponse<T> {
  data: T[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

export interface ApiError {
  statusCode: number;
  message: string;
  error?: string;
}

// ─── Settings ────────────────────────────────────────────────────────────────

export interface EnvironmentConfig {
  key: string;
  value: string;
  source: 'env' | 'db' | 'default';
}

export interface ApiKeyPolicy {
  id: string;
  name: string;
  description?: string;
}

export interface ApiKey {
  id: string;
  name: string;
  keyPrefix: string;
  policies: ApiKeyPolicy[];
  lastUsedAt?: string;
  expiresAt?: string;
  revokedAt?: string;
  createdAt: string;
}

/**
 * Response shape from POST /auth/api-keys — plaintext key shown ONCE at creation.
 */
export interface CreatedApiKey extends ApiKey {
  key: string;
  policyIds: string[];
}

export interface FeatureFlag {
  key: string;
  enabled: boolean;
  description: string;
}

// ─── Repository Indexer Types ──────────────────────────────────

export interface Repository {
  _id: string;
  name: string;
  gitUrl: string;
  gitAuthType: 'https' | 'ssh';
  neonProjectId?: string;
  neonProjectName?: string;
  daytonaResources?: { cpu: number; memory: number; disk: number };
  indexerImage: string;
  gitSecretRef?: GitSecretRef;
  isActive: boolean;
  metadata?: Record<string, unknown>;
  branches?: Branch[];
  createdAt: string;
  updatedAt: string;
}

export interface Branch {
  _id: string;
  repositoryId: string;
  name: string;
  gitBranchName: string;
  isDefault: boolean;
  neonBranchId?: string;
  neonPoolUrl?: string;
  daytonaSandboxId?: string;
  lastIndexStatus: 'none' | 'indexing' | 'completed' | 'error';
  lastIndexedAt?: string;
  lastIndexedCommitSha?: string;
  lastError?: string;
  autoStopMinutes: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface DaytonaInstance {
  _id: string;
  repositoryId: string;
  branchId: string;
  sandboxId: string;
  sandboxName?: string;
  state: 'started' | 'stopped' | 'archived' | 'error';
  resources?: { cpu: number; memory: number; disk: number };
  autoStopMinutes: number;
  lastActivityAt?: string;
  errorReason?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SearchQuery {
  query: string;
  limit?: number;
  offset?: number;
  preRankLimit?: number;
  minSimilarity?: number;
  topN?: number;
  rerank?: boolean;
  filePatterns?: string[];
}

export interface SearchResult {
  results: Array<{
    filename: string;
    content: string;
    similarity: number;
    rerankScore?: number;
    metadata: Record<string, unknown>;
  }>;
  pagination: {
    limit: number;
    offset: number;
    totalCandidates: number;
  };
}

export interface IndexStatus {
  status: 'none' | 'indexing' | 'completed' | 'error';
  totalFiles?: number;
  totalChunks?: number;
  lastIndexedAt?: string;
  lastIndexedCommitSha?: string;
  lastError?: string;
}

// ─── Meeting Types ──────────────────────────────────────────

export type MeetingStatus = 'pending' | 'active' | 'paused' | 'ended';

export interface MeetingParticipant {
  name: string;
  email?: string;
  role: string;
}

export interface MeetingSubscriber {
  subscriberService: string;
  subjectId: string;
  eventName?: string;
  subscribedAt: string;
}

export interface MeetingMetadata {
  calendarEventId?: string;
  organizer?: string;
  title?: string;
  recallRecordingId?: string;
  recallAudioMixedId?: string;
}

export interface Meeting {
  _id: string;
  meetingId: string;
  meetingUrl: string;
  status: MeetingStatus;
  botId?: string;
  botName?: string;
  participants: MeetingParticipant[];
  subscribers: MeetingSubscriber[];
  metadata: MeetingMetadata;
  startedAt?: string;
  endedAt?: string;
  createdAt: string;
  updatedAt: string;
}

// ── Planning ──────────────────────────────────────────────────────
export type TaskStatus = 'pending' | 'in_progress' | 'blocked' | 'completed' | 'cancelled';

export interface Task {
  _id: string;
  ownerId: string;
  title: string;
  description?: string;
  markdown?: string;
  status: TaskStatus;
  /** Custom board column (WS-D/PRJ-4); when set, the board groups the task here. */
  boardColumn?: string;
  parentId?: string;
  path?: string;
  depth: number;
  projectId?: string;
  source?: string;
  metadata?: Record<string, any>;
  createdAt: string;
  updatedAt: string;
}

export interface CreateTaskDto {
  ownerId: string;
  title: string;
  description?: string;
  markdown?: string;
  status?: TaskStatus;
  boardColumn?: string;
  parentId?: string;
  path?: string;
  depth?: number;
  projectId?: string;
  source?: string;
  metadata?: Record<string, any>;
}

export interface UpdateTaskDto {
  title?: string;
  description?: string;
  status?: TaskStatus;
  /** '' or null clears the custom column and returns the task to its status column. */
  boardColumn?: string | null;
  ownerId?: string;
  source?: string;
  markdown?: string;
  metadata?: Record<string, any>;
}

export interface FilterState {
  ownerIds: string[];
  statuses: TaskStatus[];
  search: string;
  depthScope: 'root' | 'all' | 'subtasks';
}

// ─── Storage ────────────────────────────────────────────────────────────────

export type StorageSourceStatus =
  'assembled' | 'assembling' | 'replicating' | 'available' | 'partial' | 'invalid' | 'deleting';

export type StorageFileStatus = 'available' | 'partial' | 'invalid' | 'deleting' | 'purged';

export type StorageUploadStatus = 'pending' | 'uploading' | 'failed' | 'completed' | 'aborted';

export type StorageReplicationStrategy = 'none' | 'round-robin' | 'fill-first' | 'mirror';

export type StorageProxyType = 'none' | 'http' | 'openvpn' | 'wireguard';

export interface StorageProviderFormField {
  name: string;
  label: string;
  type: 'string' | 'number' | 'boolean' | 'select' | 'password' | 'url' | 'record';
  required?: boolean;
  defaultValue?: unknown;
  options?: { label: string; value: string }[];
  description?: string;
}

export interface StorageProviderDescriptor {
  type: string;
  displayName: string;
  label: string;
  configSchemaJson: Record<string, unknown>;
  accountConfigSchemaJson: Record<string, unknown>;
  capabilities: { supportsByteRange: boolean; maxObjectSizeBytes?: number };
  formFields: StorageProviderFormField[];
  accountFormFields: StorageProviderFormField[];
}

export interface StorageSource {
  _id: string;
  name: string;
  type: string;
  dockerImage: string;
  supportsByteRange: boolean;
  strategy: string;
  chunkConfig: { enabled: boolean; chunkSizeMB: number };
  proxy: Record<string, unknown>;
  readEndpoint?: string;
  config: Record<string, unknown>;
  isActive: boolean;
  accountsCount?: number;
  usedBytes?: number;
  capacityBytes?: number;
  createdAt: string;
  updatedAt: string;
}

export interface StorageAccount {
  _id: string;
  sourceId: string;
  name: string;
  providerConfig: Record<string, unknown>;
  capacityBytes: number;
  usedBytes: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface StorageCollectionSettings {
  versioningEnabled: boolean;
  softDeleteDays: number;
  maxConcurrentJobs: number;
  maxFileSizeBytes?: number;
  maxTotalSizeBytes?: number;
}

export interface StorageCollection {
  _id: string;
  name: string;
  description?: string;
  sources: { sourceId: string; isDefault?: boolean }[];
  defaultSourceId: string;
  settings: StorageCollectionSettings;
  stats?: StorageStats;
  createdAt: string;
  updatedAt: string;
}

export interface StorageFileSource {
  sourceId: string;
  accountId?: string;
  storageKey?: string;
  status: StorageFileStatus;
  chunks?: { index: number; size: number; key: string }[];
  error?: string;
  lastVerifiedAt?: string;
}

export interface StorageFileVersion {
  _id: string;
  versionNumber: number;
  size: number;
  sha256?: string;
  sources?: StorageFileSource[];
  createdAt: string;
}

export interface StorageFile {
  _id: string;
  collectionId: string;
  ownerUserId?: string;
  fileName: string;
  description?: string;
  extension?: string;
  mimeType?: string;
  size: number;
  sha256?: string;
  status: StorageFileStatus;
  sources: StorageFileSource[];
  deleted?: boolean;
  deletedAt?: string;
  purgeAt?: string;
  downloadCount: number;
  versionCount?: number;
  currentVersionId?: string;
  versions?: StorageFileVersion[];
  primaryStagingKey?: string;
  createdAt: string;
  updatedAt: string;
}

/** Response of `POST /storage/files/:id/presign` (AC-12). */
export interface StorageFilePresignResult {
  url: string;
  expiresAt: string;
  oneUse: boolean;
}

export interface StorageUpload {
  _id: string;
  collectionId: string;
  fileName: string;
  fileSizeBytes: number;
  mode: 'single' | 'multipart';
  status: StorageUploadStatus;
  progress: number;
  jobId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface StorageStats {
  fileCount: number;
  totalSizeBytes: number;
  totalDownloadCount: number;
  replicatedCount: number;
}

// ─── Webhook Engine (@cluster/communication) ────────────────────────────────

export type TriggerKind = 'http' | 'internal-event';

export interface HttpSecret {
  headerName: string;
  algo: string;
}

export interface EventFilter {
  channel: string;
  eventTypes: string[];
}

/**
 * Canonical trigger shape matching the backend contract (@cluster/communication):
 * flat fields, NOT nested under `http`/`internalEvent`.
 */
export type WebhookTrigger =
  | { kind: 'http'; methods?: string[]; secret?: HttpSecret }
  | { kind: 'internal-event'; eventFilter?: EventFilter[] };

export interface SandboxConfig {
  timeoutMs?: number;
  executionQuota?: string;
  capEmits?: number;
  consoleCaptureBytes?: number;
}

/**
 * Webhook entity. The backend may expose either `_id` (Mongo) or `id`;
 * UI code should normalize via `webhookIdentifier()`.
 */
export interface Webhook {
  _id?: string;
  id: string;
  ownerUserId: string;
  name: string;
  description?: string;
  status: 'active' | 'disabled';
  code: string;
  codeHash: string;
  policyIds: string[];
  version: number;
  sandboxConfig?: SandboxConfig;
  trigger: WebhookTrigger;
  createdAt: string;
  updatedAt: string;
}

export interface CreateWebhookDto {
  name: string;
  description?: string;
  triggerKind: TriggerKind;
  trigger: WebhookTrigger;
  code: string;
  policyIds?: string[];
  sandboxConfig?: SandboxConfig;
}

export type UpdateWebhookDto = Partial<CreateWebhookDto> & {
  status?: 'active' | 'disabled';
};

export interface SandboxTestRequest {
  sampleInput: unknown;
}

export type OutcomeEvent =
  | { kind: 'http'; method: string; url: string; body?: unknown; headers?: Record<string, string> }
  | { kind: 'internal-api'; service: string; method: string; data?: unknown };

export interface SandboxTestResult {
  success: boolean;
  emits: OutcomeEvent[];
  consoleOutput: string;
  durationMs: number;
  error?: { kind: string; message: string };
}

export interface CodeValidationRequest {
  code: string;
}

export interface CodeValidationResult {
  valid: boolean;
  errors?: { line: number; column: number; message: string; kind: string }[];
}

export interface DeliveryLogEmit {
  index: number;
  kind: string;
  target: string;
  status: string;
  statusCode?: number;
  responseBody?: string;
  attempts: number;
  durationMs: number;
  createdAt: string;
}

export interface DeliveryLog {
  id: string;
  webhookId: string;
  correlationId: string;
  eventId: string;
  triggerKind: TriggerKind;
  received: unknown;
  sandbox: {
    success: boolean;
    emits: OutcomeEvent[];
    consoleOutput: string;
    durationMs: number;
    error?: unknown;
  };
  emits: DeliveryLogEmit[];
  outcome: 'success' | 'failed' | 'denied' | 'timeout' | 'quota-exceeded';
  totalDurationMs: number;
  retryCount: number;
  createdAt: string;
}

/**
 * Normalize a webhook/log identifier that the backend may expose as `_id` or `id`.
 */
export function webhookIdentifier(w: { _id?: string; id?: string }): string {
  return w.id || w._id || '';
}

// ─── Agent Platform — Provider / Model / Collection (@cluster/agent-core) ─────

export type AgentProviderType = 'openai-compatible' | 'litellm';
export type AgentModelType = 'language';
export type ModelCollectionStrategy = 'position' | 'weight' | 'random';
export type ReasoningEffort =
  'provider-default' | 'none' | 'minimal' | 'low' | 'medium' | 'high' | 'xhigh';
export type ToolChoiceMode = 'auto' | 'none' | 'required';
export type ModelToolChoice = 'auto' | 'none' | 'required' | { type: 'tool'; toolName: string };

export interface ModelTimeoutSettings {
  totalMs?: number;
  stepMs?: number;
  toolMs?: number;
}

export interface ModelSettings {
  temperature?: number;
  topP?: number;
  topK?: number;
  maxOutputTokens?: number;
  seed?: number;
  frequencyPenalty?: number;
  presencePenalty?: number;
  stopSequences?: string[];
  reasoning?: ReasoningEffort;
  toolChoice?: ModelToolChoice;
  maxSteps?: number;
  maxRetries?: number;
  timeout?: ModelTimeoutSettings;
}

/** External model provider endpoint config (agent_provider_configs). */
export interface AgentProviderConfig {
  _id: string;
  name: string;
  description?: string;
  type: AgentProviderType;
  baseURL: string;
  /** Encrypted at rest — NEVER returned to the UI (write-only apiKey on create). */
  apiKeyEncrypted?: string;
  headers?: Record<string, string>;
  organization?: string;
  project?: string;
  fetchTimeoutMs?: number;
  ownerUserId: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateProviderDto {
  name: string;
  description?: string;
  type: AgentProviderType;
  baseURL: string;
  /** WRITE-ONLY: sent on create/patch, never fetched back. */
  apiKey?: string;
  headers?: Record<string, string>;
  organization?: string;
  project?: string;
  fetchTimeoutMs?: number;
}

export type UpdateProviderDto = Partial<CreateProviderDto>;

/** Shape of POST /agent/providers/:id/test (AF-10) — models are plain ids. */
export interface ProviderTestResult {
  ok: boolean;
  models?: string[];
  latencyMs?: number;
  error?: string;
}

/** Per-model generation config (agent_model_configs). */
export interface AgentModelConfig {
  _id: string;
  name: string;
  description?: string;
  providerConfigId: string;
  modelName: string;
  modelType: AgentModelType;
  settings?: ModelSettings;
  /** Free JSON merged into providerOptions.<provider> last (AF-6 passthrough). */
  extraProviderOptions?: Record<string, unknown>;
  ownerUserId: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateModelDto {
  name: string;
  description?: string;
  providerConfigId: string;
  modelName: string;
  modelType?: AgentModelType;
  settings?: Partial<ModelSettings>;
  extraProviderOptions?: Record<string, unknown>;
}

export type UpdateModelDto = Partial<CreateModelDto>;

/** Shape of POST /agent/models/:id/test (AF-10). */
export interface ModelTestResult {
  ok: boolean;
  latencyMs?: number;
  finishReason?: string;
  error?: string;
}

export interface ModelCollectionEntry {
  modelConfigId: string;
  weight?: number;
  position?: number;
}

export interface ModelCollectionFailover {
  enabled: boolean;
  maxAttemptsPerRun?: number;
}

/** Ordered/priced model set with selection + failover strategy (agent_model_collections). */
export interface AgentModelCollection {
  _id: string;
  name: string;
  description?: string;
  strategy: ModelCollectionStrategy;
  entries: ModelCollectionEntry[];
  failover: ModelCollectionFailover;
  ownerUserId: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateModelCollectionDto {
  name: string;
  description?: string;
  strategy?: ModelCollectionStrategy;
  entries?: ModelCollectionEntry[];
  failover?: Partial<ModelCollectionFailover>;
}

export type UpdateModelCollectionDto = Partial<CreateModelCollectionDto>;

// ─── Agent Platform — Factory (agent_factories) ───────────────────────────────

export interface FactoryToolConfig {
  internalApi: boolean;
  extraServices?: string[];
}

export interface FactoryStructuredOutputCfg {
  enabled: boolean;
  jsonSchema?: Record<string, unknown>;
  name?: string;
  description?: string;
}

export interface FactoryMemoryCfg {
  collectionIds: string[];
}

export interface FactoryInitialResource {
  type: string;
  id: string;
  inject?: boolean;
}

export interface FactoryBootstrapStage {
  factoryId: string;
  name?: string;
  inputTemplate?: Record<string, unknown>;
}

export interface FactoryBootstrapCfg {
  enabled: boolean;
  stages: FactoryBootstrapStage[];
}

export interface FactoryCompactionCfg {
  enabled: boolean;
  prompt?: string;
  tokenThreshold?: number;
  messageCountThreshold?: number;
  byteThreshold?: number;
  webhookId?: string;
}

export interface FactoryRunDefaultsCfg {
  maxSteps?: number;
  toolChoice?: Record<string, unknown>;
  temperature?: number;
  maxOutputTokens?: number;
  timeout?: ModelTimeoutSettings;
  extraProviderOptions?: Record<string, unknown>;
}

/** Reusable agent blueprint (AF-1..14). */
export interface AgentFactory {
  _id: string;
  name: string;
  description?: string;
  ownerUserId: string;
  projectId?: string;
  instructions: string;
  modelCollectionId: string;
  policyIds: string[];
  toolConfig: FactoryToolConfig;
  structuredOutput: FactoryStructuredOutputCfg;
  memory: FactoryMemoryCfg;
  skillsCollectionId?: string;
  initialResources: FactoryInitialResource[];
  bootstrap: FactoryBootstrapCfg;
  compaction: FactoryCompactionCfg;
  runDefaults: FactoryRunDefaultsCfg;
  modelType: AgentModelType;
  tags?: string[];
  isActive: boolean;
  version?: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateFactoryDto {
  name: string;
  description?: string;
  projectId?: string;
  instructions: string;
  modelCollectionId: string;
  policyIds: string[];
  toolConfig?: Partial<FactoryToolConfig>;
  structuredOutput?: Partial<FactoryStructuredOutputCfg>;
  memory?: Partial<FactoryMemoryCfg>;
  skillsCollectionId?: string;
  initialResources?: FactoryInitialResource[];
  bootstrap?: Partial<FactoryBootstrapCfg>;
  compaction?: Partial<FactoryCompactionCfg>;
  runDefaults?: Partial<FactoryRunDefaultsCfg>;
  tags?: string[];
  isActive?: boolean;
}

export type UpdateFactoryDto = Partial<CreateFactoryDto>;

/** Agent-trigger kinds (agent-core) — distinct from the webhook engine's `TriggerKind`. */
export type AgentTriggerKind = 'cron' | 'webhook' | 'event';

export interface TriggerEventFilter {
  channel: string;
  eventTypes: string[];
}

/** Binds a factory to a communication webhook (agent_trigger_bindings). */
export interface TriggerBinding {
  _id: string;
  factoryId: string;
  kind: AgentTriggerKind;
  webhookId: string;
  cron?: string;
  eventFilter?: TriggerEventFilter[];
  payloadTemplate?: Record<string, unknown>;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateTriggerDto {
  factoryId: string;
  kind: AgentTriggerKind;
  cron?: string;
  eventFilter?: TriggerEventFilter[];
  payloadTemplate?: Record<string, unknown>;
}

export type UpdateTriggerDto = Partial<Omit<CreateTriggerDto, 'factoryId'>> & {
  isActive?: boolean;
};

// ─── Agent Platform — Runtime (Conversations / Messages / Runs) ───────────────

export type ConversationStatus =
  'pending' | 'running' | 'idle' | 'paused' | 'stopped' | 'completed' | 'error';

export type ConversationControl = 'none' | 'stop' | 'pause' | 'compact';
export type CompactionState = 'none' | 'requested' | 'running';
export type BootstrapState = 'none' | 'running' | 'done';
export type ModelOverrideReason = 'manual' | 'failover';
export type CleanupOn = 'completed' | 'stopped' | 'always';

export interface ConversationModelState {
  cursor: number;
  tried: string[];
}

export interface ConversationModelOverride {
  modelConfigId: string;
  reason: ModelOverrideReason;
  at?: string;
}

export interface ConversationUsage {
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
}

export interface ConversationEphemeralResource {
  type: string;
  id: string;
  cleanupOn: CleanupOn;
}

export interface ConversationForkOf {
  conversationId: string;
  messagePosition: number;
}

/** A single agent conversation — ALL run state lives here (agent_conversations). */
export interface Conversation {
  _id: string;
  id?: string;
  factoryId: string;
  name?: string;
  ownerUserId: string;
  projectId?: string;
  status: ConversationStatus;
  modelConfigId: string;
  modelCollectionId: string;
  modelState: ConversationModelState;
  modelOverride?: ConversationModelOverride;
  policyIds?: string[];
  overrides?: Record<string, unknown>;
  pendingCount: number;
  control: ConversationControl;
  pausedAtStep?: number;
  compactionState: CompactionState;
  ephemeralResources?: ConversationEphemeralResource[];
  forkOf?: ConversationForkOf;
  bootstrapState: BootstrapState;
  bootstrapOutputs?: Record<string, unknown>;
  hasPending: boolean;
  activeJobId?: string;
  usage: ConversationUsage;
  usageKeep?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface CreateConversationDto {
  factoryId: string;
  name?: string;
  payload?: Record<string, unknown>;
  overrides?: Record<string, unknown>;
  fileRefs?: AgentMessageFileRef[];
}

export type MessageKind = 'user' | 'assistant' | 'system' | 'tool';
export type MessageStatus = 'pending' | 'processing' | 'done' | 'failed';

export interface AgentMessageFileRef {
  fileId: string;
  mediaType: string;
  inject?: boolean;
}

/** One entry of the append-only per-conversation message log (agent_messages). */
export interface AgentMessage {
  _id: string;
  conversationId: string;
  seq: number;
  kind: MessageKind;
  status: MessageStatus;
  /** Codec-serializable ModelMessage fragment OR structured output. */
  content: Record<string, unknown> | string;
  fileRefs?: AgentMessageFileRef[];
  output?: Record<string, unknown>;
  runId?: string;
  createdAt: string;
  updatedAt: string;
}

export type RunStatus = 'running' | 'completed' | 'failed' | 'aborted';

export interface RunError {
  code: string;
  message: string;
  statusCode?: number;
}

/** One run of a conversation's FIFO chain (agent_runs). */
export interface Run {
  _id: string;
  conversationId: string;
  seq: number;
  modelConfigId?: string;
  jobId?: string;
  status: RunStatus;
  steps: number;
  usage?: Record<string, unknown>;
  error?: RunError;
  startedAt?: string;
  finishedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface MessageVersion {
  _id: string;
  conversationId: string;
  versionNumber: number;
  reason: 'step' | 'compaction' | 'fork' | 'manual';
  messages: unknown[];
  createdAt: string;
}

/** Streamed part shapes published on `agent:stream` (text/reasoning deltas etc). */
export type AgentStreamPart =
  | {
      type: 'text-delta';
      text: string;
      delta?: string;
    }
  | {
      type: 'reasoning-delta';
      text: string;
      delta?: string;
    }
  | {
      type: 'step-start';
      step?: number;
      maxSteps?: number;
      modelConfigId?: string;
    }
  | {
      type: 'step-finish';
      step?: number;
      tokens?: number;
    }
  | {
      type: 'tool-call';
      toolCallId?: string;
      toolName?: string;
      input?: unknown;
    }
  | {
      type: 'tool-result';
      toolCallId?: string;
      toolName?: string;
      output?: unknown;
    }
  | {
      type: 'error';
      error?: string;
      code?: string;
    }
  | { type: string; [key: string]: unknown };

export interface AgentStreamEnvelope {
  t?: 'part';
  part?: AgentStreamPart;
  at?: string;
  conversationId?: string;
}

// ─── Organization / User Groups ───────────────────────────────────────────────

export interface Organization {
  _id: string;
  name: string;
  description?: string;
  ownerUserId: string;
  settings?: Record<string, unknown>;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export type OrgRoleName = 'owner' | 'admin' | 'member';

export interface OrgMember {
  _id?: string;
  orgId: string;
  userId: string;
  orgRole: OrgRoleName;
  roleIds?: string[];
  addedBy?: string;
  addedAt: string;
}

export interface OrganizationFull extends Organization {
  members?: OrgMember[];
}

export type UserGroupRole = 'owner' | 'member';

export interface UserGroup {
  _id: string;
  name: string;
  description?: string;
  orgId?: string;
  ownerUserId: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface UserGroupMember {
  _id?: string;
  groupId: string;
  userId: string;
  role?: UserGroupRole;
  addedBy?: string;
  addedAt: string;
}

// ─── Roles ────────────────────────────────────────────────────────────────────

export interface PolicySpec {
  name: string;
  statements: Array<Record<string, unknown>>;
  ttlSeconds: number;
}

export interface Role {
  _id: string;
  name: string;
  description?: string;
  orgId?: string;
  projectId?: string;
  ownerUserId: string;
  policySpecs: PolicySpec[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export type AssignmentStatus = 'active' | 'revoked';

export interface RoleAssignment {
  _id?: string;
  roleId: string;
  userId: string;
  orgId?: string;
  status: AssignmentStatus;
  policyIds: string[];
  assignedBy?: string;
  assignedAt?: string;
  expiresAt?: string;
  revokedBy?: string;
  revokedAt?: string;
}

// ─── Projects ─────────────────────────────────────────────────────────────────

export type ProjectResourceType =
  | 'meeting'
  | 'planning'
  | 'storage-collection'
  | 'repository'
  | 'conversation'
  | 'indexer-source'
  | 'voice'
  | 'file';

export interface Project {
  _id: string;
  name: string;
  description?: string;
  orgId?: string;
  ownerUserId: string;
  settings?: Record<string, unknown>;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ProjectResource {
  _id: string;
  projectId: string;
  type: ProjectResourceType;
  refId: string;
  name?: string;
  metadata?: Record<string, unknown>;
  addedBy: string;
  addedAt: string;
}

// ─── Approvals ────────────────────────────────────────────────────────────────

export type ApprovalStatus = 'pending' | 'approved' | 'rejected' | 'expired' | 'cancelled';

export interface ApprovalRequester {
  conversationId?: string;
  userId?: string;
  bulkOperationId?: string;
}

export interface RequestedAction {
  service: string;
  method: string;
  payloadSummary?: Record<string, unknown>;
}

export interface ApprovalChoice {
  id: string;
  label: string;
}

export interface Approval {
  _id: string;
  projectId: string;
  title: string;
  description?: string;
  requester: ApprovalRequester;
  requestedActions: RequestedAction[];
  choices: ApprovalChoice[];
  status: ApprovalStatus;
  decisionChoiceId?: string;
  decidedBy?: string;
  decidedAt?: string;
  policyIdsScope: string[];
  ephemeralPolicyId?: string;
  ephemeralTokenExpirySeconds: number;
  expiresAt: string;
  createdAt: string;
  updatedAt: string;
}

// ─── Secrets (Vaults) ─────────────────────────────────────────────────────────

/** Vault entry as exposed by GET — KEYS ONLY, never values (SEC). */
export interface VaultEntryRef {
  key: string;
  updatedAt: string;
}

export interface Vault {
  _id: string;
  name: string;
  description?: string;
  ownerUserId: string;
  projectId?: string;
  /**
   * Omitted by `GET /secrets/vaults` (list projection is `.select('-entries')`)
   * — only the vault detail carries entry key names. Always guard before use.
   */
  entries?: VaultEntryRef[];
  settings: { allowAgentRead: boolean };
  createdAt: string;
  updatedAt: string;
}

export interface VaultReadResponse {
  key: string;
  value: string;
}

// ─── Voice ────────────────────────────────────────────────────────────────────

export interface Voice {
  _id?: string;
  /** The voice service returns DTOs keyed by `id` (its own toDto mapping). */
  id: string;
  name: string;
  description?: string;
  language: string;
  fileId?: string;
  providerVoiceId?: string;
  ttsModel: string;
  ownerUserId: string;
  projectId?: string;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateVoiceDto {
  name: string;
  description?: string;
  language?: string;
  fileId?: string;
  /** DashScope voice id already enrolled/cloned; empty → service default. */
  providerVoiceId?: string;
  /** TTS model override; empty → service default. */
  ttsModel?: string;
  projectId?: string;
}

export interface UpdateVoiceDto {
  name?: string;
  description?: string;
  language?: string;
  fileId?: string;
  providerVoiceId?: string;
  ttsModel?: string;
  projectId?: string;
  isActive?: boolean;
}

/** Voice resolution source reported by the backend after synthesis. */
export type VoiceSource = 'providerVoiceId' | 'clone' | 'default';

export interface VoiceTestResult {
  fileId: string;
  url?: string;
  durationMs: number;
  /** How the provider voice was resolved; `default` means the clone was not used. */
  source?: VoiceSource;
}

// ─── Daytona sandboxes ────────────────────────────────────────────────────────

export type SandboxState = 'started' | 'stopped' | 'archived' | 'error' | 'unknown';

export interface SandboxResources {
  cpu: number;
  memory: number;
  disk: number;
}

export interface DaytonaSandbox {
  _id: string;
  name: string;
  sandboxId: string;
  state: SandboxState;
  resources?: SandboxResources;
  repositoryId?: string;
  branchId?: string;
  factoryId?: string;
  conversationId?: string;
  /** Encrypted env — never returned to the UI (values are write-only). */
  env?: Record<string, unknown>;
  autoStopMinutes: number;
  autoArchiveMinutes: number;
  lastActivityAt?: string;
  ownerUserId: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateSandboxDto {
  name: string;
  repositoryId?: string;
  branchId?: string;
  factoryId?: string;
  conversationId?: string;
  resources?: SandboxResources;
  env?: Record<string, string>;
  autoStopMinutes?: number;
  autoArchiveMinutes?: number;
}

export type UpdateSandboxDto = Partial<CreateSandboxDto>;

export interface SandboxExecResult {
  exitCode: number;
  stdout: string;
  stderr: string;
}

// ─── Bulk operations (@cluster/communication) ─────────────────────────────────

export type BulkOperationStatus =
  'draft' | 'pending-approval' | 'running' | 'completed' | 'timeout' | 'cancelled' | 'failed';

export interface BulkAction {
  service: string;
  method: string;
  payload: Record<string, unknown>;
}

export interface BulkTargetEvent {
  service: string;
  method: string;
  payloadTemplate: Record<string, unknown>;
}

export interface BulkListenCompletion {
  minEvents?: number;
  untilEvent?: string;
}

export interface BulkListener {
  webhookId: string;
  token: string;
  completion?: BulkListenCompletion;
}

export interface BulkOperation {
  _id: string;
  name: string;
  description?: string;
  ownerUserId: string;
  projectId?: string;
  status: BulkOperationStatus;
  timeoutMs: number;
  requiresApproval: boolean;
  approvalId?: string;
  approvalRequestedAt?: string;
  policyIds: string[];
  actions: BulkAction[];
  targetEvents: BulkTargetEvent[];
  outputs?: unknown[];
  flowJobId?: string;
  startedAt?: string;
  finishedAt?: string;
  listener?: BulkListener;
  createdAt: string;
  updatedAt: string;
}

/** Child state as returned by GET /bulk-operations/:id/status (BULK-3). */
export interface BulkChildState {
  jobId?: string;
  name?: string;
  status: string;
  returnvalue?: unknown;
  result?: unknown;
  error?: string;
}

export interface BulkOperationStatusDetail {
  id: string;
  status: BulkOperationStatus;
  flowJobId?: string;
  childStates: BulkChildState[];
  elapsedMs?: number;
}

// ─── Indexer ──────────────────────────────────────────────────────────────────

export type IndexerSourceType = 'git' | 'collection';
export type IndexerSourceStatus = 'idle' | 'syncing' | 'error';

export interface IndexerSource {
  _id: string;
  type: IndexerSourceType;
  repositoryId?: string;
  branchName?: string;
  collectionId?: string;
  mediaEnabled?: boolean;
  lastSyncedCommitSha?: string;
  status: IndexerSourceStatus;
  lastSyncAt?: string;
  lastError?: string;
  ownerUserId?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface IndexerGraphNode {
  id: string;
  type: 'source' | 'table' | 'flow' | 'collection' | 'repo';
  label: string;
  stats: { chunks?: number; files?: number; lastSync?: string };
}

export interface IndexerGraphEdge {
  from: string;
  to: string;
  kind: 'writes' | 'reads' | 'derived';
}

export interface IndexerGraph {
  nodes: IndexerGraphNode[];
  edges: IndexerGraphEdge[];
}

export interface IndexerSearchRequest {
  query: string;
  sourceType?: 'git' | 'collection';
  sourceKey?: string;
  limit?: number;
  offset?: number;
  minSimilarity?: number;
  rerank?: boolean;
}

export interface IndexerSearchHit {
  id?: string;
  sourceType?: string;
  sourceKey?: string;
  /** Backend field name (unified search service). */
  filePath: string;
  content: string;
  /** Backend field name — cosine similarity. */
  similarity: number;
  /** Compatibility alias for older search providers. */
  score?: number;
  rerankScore?: number;
  file_path?: string;
  chunk_index?: number;
  metadata?: Record<string, unknown>;
}

export interface IndexerSearchResponse {
  results: IndexerSearchHit[];
  pagination: { limit: number; offset: number; totalCandidates: number };
  vector: boolean;
}

// ─── Agent-tooling / misc contracts ───────────────────────────────────────────

export interface ConversationDispatchResult {
  conversationId: string;
  factoryId: string;
  name?: string;
}

export interface AgentQueueStats {
  [queueGroup: string]: {
    waiting: number;
    active: number;
    completed: number;
    failed: number;
    delayed: number;
    paused?: number;
  };
}

export interface IdEntity {
  id?: string;
  _id?: string;
}

// ─── Repositories: commits / pull-requests / mirror (GIT-2/4) ─────────────────

export interface Commit {
  _id: string;
  repositoryId: string;
  branchName: string;
  sha: string;
  parentShas: string[];
  author: string;
  authorEmail?: string;
  message: string;
  committedAt: string;
  filesChanged: number;
  createdAt: string;
  updatedAt: string;
}

export type PullRequestStatus = 'open' | 'reviewing' | 'merged' | 'closed';
export type PullRequestReviewEvent = 'APPROVE' | 'REQUEST_CHANGES' | 'COMMENT';

export interface PullRequest {
  _id: string;
  repositoryId: string;
  provider: 'github' | 'gitea';
  providerNumber: number;
  title: string;
  body?: string;
  headBranch: string;
  baseBranch: string;
  state: 'open' | 'closed';
  status: PullRequestStatus;
  agentConversationId?: string;
  reviewConversationId?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface PullRequestReview {
  id: string;
  user?: string;
  state?: string;
  body?: string;
  submittedAt?: string;
}

export interface CreatePullRequestDto {
  title: string;
  headBranch: string;
  baseBranch: string;
  body?: string;
}

export interface UpdatePullRequestDto {
  state?: 'open' | 'closed';
  status?: PullRequestStatus;
  title?: string;
  body?: string;
  agentConversationId?: string;
  reviewConversationId?: string;
}

export interface CommitSyncResult {
  repositoryId: string;
  branches: Array<{
    branchId: string;
    branchName: string;
    gitBranchName: string;
    headSha: string;
    commitsSynced: number;
    upToDate: boolean;
  }>;
  totalCommitsSynced: number;
}

/** Vault-backed git credentials — stored on the repository (GIT-2). */
export interface GitSecretRef {
  vaultId: string;
  userKey?: string;
  tokenKey?: string;
}

// ─── Core registry (policy-builder action autocomplete) ───────────────────────

export interface RegistryService {
  name: string;
  queueName?: string;
  description?: string;
  status?: ServiceStatus;
  endpoints?: EndpointDescriptor[];
}

// ─── Policy builder (PB-1) ────────────────────────────────────────────────────

export interface PolicyRateLimit {
  requestsPerMinute?: number;
  requestsPerHour?: number;
}

export interface PolicyExpressionValidation {
  valid: boolean;
  error?: string;
  referencedFields?: string[];
}

export interface PolicyEvaluateRequest {
  userId: string;
  action: string;
  resource: string;
  context?: Record<string, unknown>;
  policyIds?: string[];
}

export interface PolicyEvaluateResult {
  allowed: boolean;
  rateLimit?: PolicyRateLimit;
}

/** JSEP-compatible condition expression row (backend policy.model conditions). */
export interface PolicyConditionExpression {
  expression: string;
  description?: string;
}

export interface PolicyDraft {
  name: string;
  description?: string;
  statements: PolicyStatement[];
  priority?: number;
  parentPolicyId?: string;
  effectiveAt?: string;
  ttlSeconds?: number;
  rateLimit?: PolicyRateLimit;
}

export interface PolicyTemplate {
  id: string;
  name: string;
  description: string;
  policy: {
    name: string;
    description?: string;
    statements: PolicyStatement[];
    priority?: number;
    rateLimit?: PolicyRateLimit;
    ttlSeconds?: number;
  };
}

/** Policy list entry returned by authorization GET /policies (paginated). */
export interface PolicySummary {
  _id: string;
  name: string;
  description?: string;
  ownerUserId: string;
  statements?: PolicyStatement[];
  priority?: number;
  isActive?: boolean;
  expiresAt?: string;
  rateLimit?: PolicyRateLimit;
  createdAt?: string;
}

// ─── Storage markdown / content edits (ST-3/ST-4) ─────────────────────────────

export interface CreateMarkdownDto {
  name: string;
  content: string;
  description?: string;
}

export interface SaveMarkdownDto {
  content: string;
  expectedVersion?: number;
}

export interface PartialFileContentOp {
  op: 'append' | 'overwrite-range' | 'remove-range';
  position?: number;
  length?: number;
  /** base64-encoded bytes for append / overwrite-range. */
  data?: string;
  expectedVersion?: number;
}

/** Response of PATCH /storage/files/:id/content — async file-edit enqueued. */
export interface FileEditJobResult {
  jobId: string;
  fileId: string;
}
